class FacetFiltersForm extends HTMLElement {
    constructor() {
        super();
        this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

        this.debouncedOnSubmit = debounce((event) => {
            this.onSubmitHandler(event);
        }, 800);

        const facetForm = this.querySelector('form');
        facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));
        facetForm.addEventListener('input', (event) => {
            if (event.target.name === 'sort_by') FacetFiltersForm.renderSortLabel();
        });

        const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
        if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
    }

    static setListeners() {
        const onHistoryChange = (event) => {
            const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
            if (searchParams === FacetFiltersForm.searchParamsPrev) return;
            FacetFiltersForm.renderPage(searchParams, null, false);
        };
        window.addEventListener('popstate', onHistoryChange);

        const productGridContainer = document.getElementById('ProductGridContainer');
        if (productGridContainer) {
            productGridContainer.addEventListener('click', FacetFiltersForm.onPaginationClick);
        }
    }

    static onPaginationClick(event) {
        const link = event.target.closest('[data-pagination-link]');
        if (!link) return;

        event.preventDefault();
        const searchParams =
            link.href.indexOf('?') === -1 ? '' : link.href.slice(link.href.indexOf('?') + 1);
        FacetFiltersForm.renderPage(searchParams, null);
    }

    static renderPage(searchParams, event, updateURLHash = true) {
        FacetFiltersForm.searchParamsPrev = searchParams;
        const sections = FacetFiltersForm.getSections();
        FacetFiltersForm.setGridLoading(true);
        document.dispatchEvent(new Event('loading:start'));

        sections.forEach((section) => {
            const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
            const filterDataUrl = (element) => element.url === url;

            FacetFiltersForm.filterData.some(filterDataUrl)
                ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
                : FacetFiltersForm.renderSectionFromFetch(url, event);
        });

        if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
    }

    static setGridLoading(isLoading) {
        const container = document.getElementById('ProductGridContainer');
        if (!container) return;
        container.toggleAttribute('data-loading', isLoading);
        container.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    }

    static clearGridLoading() {
        FacetFiltersForm.setGridLoading(false);
        document.dispatchEvent(new Event('loading:end'));
    }

    static renderSectionFromFetch(url, event) {
        fetch(url)
            .then((response) => response.text())
            .then((responseText) => {
                const html = responseText;
                FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
                FacetFiltersForm.renderFilters(html, event);
                FacetFiltersForm.renderProductGridContainer(html);
                FacetFiltersForm.renderProductCount(html);
                FacetFiltersForm.renderSortLabel();
            })
            .finally(() => {
                FacetFiltersForm.clearGridLoading();
            });
    }

    static renderSectionFromCache(filterDataUrl, event) {
        const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        FacetFiltersForm.renderSortLabel();
        FacetFiltersForm.clearGridLoading();
    }

    static renderProductGridContainer(html) {
        const gridSize = document.getElementById('product-grid')?.dataset.gridSize;
        document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
            .parseFromString(html, 'text/html')
            .getElementById('ProductGridContainer').innerHTML;
        const nextGrid = document.getElementById('product-grid');
        if (nextGrid && gridSize) nextGrid.dataset.gridSize = gridSize;
    }

    static renderProductCount(html) {
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const parsedCount = parsed.getElementById('ProductCount');
        const container = document.getElementById('ProductCount');
        if (parsedCount && container) container.innerHTML = parsedCount.innerHTML;

        const parsedShow = parsed.querySelector('[data-facets-show-results]');
        const showBtn = document.querySelector('[data-facets-show-results]');
        if (parsedShow && showBtn) showBtn.textContent = parsedShow.textContent;
    }

    static renderSortLabel() {
        const checked = document.querySelector('#FacetFiltersForm input[name="sort_by"]:checked');
        const name = checked?.closest('label')?.querySelector('.facets__option-name')?.textContent?.trim();
        const target = document.querySelector('[data-collection-sort-label]');
        if (name && target) target.textContent = name;
    }

    static renderFilters(html, event) {
        const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
        const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll('#FacetFiltersForm [data-details-filter]');
        const facetDetailsElementsFromDom = document.querySelectorAll('#FacetFiltersForm [data-details-filter]');

        // Remove facets that are no longer returned from the server
        Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
            if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
                currentElement.remove();
            }
        });

        const matchesId = (element) => {
            const jsFilter = event ? event.target.closest('[data-details-filter]') : undefined;
            return jsFilter ? element.id === jsFilter.id : false;
        };

        const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
        const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

        facetsToRender.forEach((elementToRender) => {
            const currentElement = document.getElementById(elementToRender.id);
            if (currentElement?.hasAttribute('open')) {
                elementToRender.setAttribute('open', '');
            }
        });

        facetsToRender.forEach((elementToRender, index) => {
            const currentElement = document.getElementById(elementToRender.id);

            if (currentElement) {
                currentElement.innerHTML = elementToRender.innerHTML;
            } else {
                if (index > 0) {
                    const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
                    if (elementToRender.className === previousElementClassName) {
                        document.getElementById(previousElementId).after(elementToRender);
                        return;
                    }
                }

                if (elementToRender.parentElement) {
                    document.querySelector(`#${elementToRender.parentElement.id} [data-details-filter]`).before(elementToRender);
                }
            }
        });

        FacetFiltersForm.renderActiveFacets(parsedHTML);
        FacetFiltersForm.renderAdditionalElements(parsedHTML);

        if (countsToRender) {
            const closestJSFilterID = event.target.closest('[data-details-filter]').id;

            if (closestJSFilterID) {
                FacetFiltersForm.renderCounts(countsToRender, event.target.closest('[data-details-filter]'));

                const newFacetDetailsElement = document.getElementById(closestJSFilterID);
                const newElementToActivate = newFacetDetailsElement.querySelector('[data-summary-filter]');

                const isTextInput = event.target.getAttribute('type') === 'text';

                if (newElementToActivate && !isTextInput) newElementToActivate.focus();
            }
        }
    }

    static renderActiveFacets(html) {
        const activeFacetElementSelectors = ['[data-active-facets]'];

        activeFacetElementSelectors.forEach((selector) => {
            const activeFacetsElement = html.querySelector(selector);
            if (!activeFacetsElement) return;
            document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
        });
    }

    static renderAdditionalElements(html) { }

    static renderCounts(source, target) {
        const targetSummary = target.querySelector('[data-summary-filter]');
        const sourceSummary = source.querySelector('[data-summary-filter]');

        if (sourceSummary && targetSummary) {
            targetSummary.outerHTML = sourceSummary.outerHTML;
        }

        const targetHeaderElement = target.querySelector('[data-header-filter]');
        const sourceHeaderElement = source.querySelector('[data-header-filter]');

        if (sourceHeaderElement && targetHeaderElement) {
            targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
        }

        const targetWrapElement = target.querySelector('[data-fieldset-filter]');
        const sourceWrapElement = source.querySelector('[data-fieldset-filter]');

        if (sourceWrapElement && targetWrapElement) {
            const isShowingMore = Boolean(target.querySelector('show-more-button [data-label-show-more].facets__show-more-is-hidden'));

            if (isShowingMore) {
                sourceWrapElement.querySelectorAll('.facets__option-item-collapsed').forEach((hiddenItem) => {
                    hiddenItem.classList.remove('facets__option-item-collapsed');
                    hiddenItem.classList.add('facets__option-item');
                });
            }

            targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
        }
    }

    static updateURLHash(searchParams) {
        history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
    }

    static getSections() {
        return [
            {
                section: document.getElementById('product-grid').dataset.id,
            },
        ];
    }

    createSearchParams(form) {
        const formData = new FormData(form);
        return new URLSearchParams(formData).toString();
    }

    onSubmitForm(searchParams, event) {
        FacetFiltersForm.renderPage(searchParams, event);
    }

    onSubmitHandler(event) {
        event.preventDefault();
        const sortFilterForms = document.querySelectorAll('facet-filters-form form');

        const forms = [];

        sortFilterForms.forEach((form) => {
            if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm') {
                forms.push(this.createSearchParams(form));
            }
        });
        this.onSubmitForm(forms.join('&'), event);
    }

    onActiveFilterClick(event) {
        event.preventDefault();
        const url =
            event.currentTarget.href.indexOf('?') == -1
                ? ''
                : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
        FacetFiltersForm.renderPage(url);
    }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);

customElements.define('facet-filters-form', FacetFiltersForm);

FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
    constructor() {
        super();
        this.minText = this.querySelector('[data-price-input="min"]');
        this.maxText = this.querySelector('[data-price-input="max"]');
        this.minSlider = this.querySelector('[data-price-slider="min"]');
        this.maxSlider = this.querySelector('[data-price-slider="max"]');

        this.minText?.addEventListener('change', this.onTextChange.bind(this));
        this.maxText?.addEventListener('change', this.onTextChange.bind(this));
        this.minText?.addEventListener('keydown', this.onKeyDown.bind(this));
        this.maxText?.addEventListener('keydown', this.onKeyDown.bind(this));
        this.minSlider?.addEventListener('input', this.onSliderInput.bind(this));
        this.maxSlider?.addEventListener('input', this.onSliderInput.bind(this));
        this.minSlider?.addEventListener('pointerdown', () => this.bringToFront(this.minSlider));
        this.maxSlider?.addEventListener('pointerdown', () => this.bringToFront(this.maxSlider));

        this.syncRangeBounds();
        this.setMinAndMaxValues();
        this.syncSlidersFromText();
        this.updateThumbState();
    }

    parseValue(value, fallback = 0) {
        const normalized = String(value ?? '').trim().replace(/\s/g, '');
        if (!normalized) return fallback;

        let parsed;
        if (normalized.includes(',') && normalized.includes('.')) {
            parsed =
                normalized.lastIndexOf(',') > normalized.lastIndexOf('.')
                    ? Number(normalized.replace(/\./g, '').replace(',', '.'))
                    : Number(normalized.replace(/,/g, ''));
        } else if (normalized.includes(',')) {
            parsed = Number(normalized.replace(',', '.'));
        } else {
            parsed = Number(normalized);
        }

        return Number.isFinite(parsed) ? parsed : fallback;
    }

    formatValue(value) {
        return String(Math.round(value));
    }

    rangeCeiling() {
        return this.parseValue(this.maxText?.getAttribute('data-max'), this.parseValue(this.maxSlider?.max, 0));
    }

    syncRangeBounds() {
        const ceil = this.rangeCeiling();
        [this.minSlider, this.maxSlider].forEach((slider) => {
            if (!slider) return;
            slider.min = '0';
            slider.max = String(ceil);
        });
    }

    bringToFront(slider) {
        if (this.minSlider) this.minSlider.style.zIndex = slider === this.minSlider ? '3' : '1';
        if (this.maxSlider) this.maxSlider.style.zIndex = slider === this.maxSlider ? '3' : '1';
    }

    onSliderInput(event) {
        const ceil = this.rangeCeiling();
        let min = this.parseValue(this.minSlider.value, 0);
        let max = this.parseValue(this.maxSlider.value, ceil);

        if (event.currentTarget === this.minSlider && min > max) {
            min = max;
            this.minSlider.value = min;
        } else if (event.currentTarget === this.maxSlider && max < min) {
            max = min;
            this.maxSlider.value = max;
        }

        if (this.minText) this.minText.value = min <= 0 ? '' : this.formatValue(min);
        if (this.maxText) this.maxText.value = max >= ceil ? '' : this.formatValue(max);
        this.setMinAndMaxValues();
        this.updateThumbState();
    }

    onTextChange(event) {
        this.adjustToValidValues(event.currentTarget);
        this.setMinAndMaxValues();
        this.syncSlidersFromText();
        this.updateThumbState();
    }

    onKeyDown(event) {
        if (event.metaKey) return;

        const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
        if (!event.key.match(pattern)) event.preventDefault();
    }

    syncSlidersFromText() {
        const ceil = this.rangeCeiling();
        const min = this.minText?.value === '' ? 0 : this.parseValue(this.minText?.value, 0);
        const max = this.maxText?.value === '' ? ceil : this.parseValue(this.maxText?.value, ceil);
        if (this.minSlider) this.minSlider.value = min;
        if (this.maxSlider) this.maxSlider.value = max;
    }

    updateThumbState() {
        const ceil = this.rangeCeiling();
        const min = this.parseValue(this.minSlider?.value, 0);
        const max = this.parseValue(this.maxSlider?.value, ceil);
        this.minSlider?.classList.toggle('is-changed', min > 0);
        this.maxSlider?.classList.toggle('is-changed', max < ceil);

        const start = ceil > 0 ? (min / ceil) * 100 : 0;
        const end = ceil > 0 ? (max / ceil) * 100 : 100;
        this.style.setProperty('--range-start', `${start}%`);
        this.style.setProperty('--range-end', `${end}%`);
    }

    setMinAndMaxValues() {
        if (!this.minText || !this.maxText) return;
        const ceil = this.rangeCeiling();
        if (this.maxText.value) this.minText.setAttribute('data-max', this.maxText.value);
        if (this.minText.value) this.maxText.setAttribute('data-min', this.minText.value);
        if (this.minText.value === '') this.maxText.setAttribute('data-min', 0);
        if (this.maxText.value === '') this.minText.setAttribute('data-max', ceil);
    }

    adjustToValidValues(input) {
        const value = this.parseValue(input.value, NaN);
        if (!Number.isFinite(value)) return;
        const min = this.parseValue(input.getAttribute('data-min'), 0);
        const max = this.parseValue(input.getAttribute('data-max'), this.rangeCeiling());

        if (value < min) input.value = this.formatValue(min);
        if (value > max) input.value = this.formatValue(max);
    }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
    constructor() {
        super();
        const facetLink = this.querySelector('a');
        facetLink.setAttribute('role', 'button');
        facetLink.addEventListener('click', this.closeFilter.bind(this));
        facetLink.addEventListener('keyup', (event) => {
            event.preventDefault();
            if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
        });
    }

    closeFilter(event) {
        event.preventDefault();
        const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
        form.onActiveFilterClick(event);
    }
}

customElements.define('facet-remove', FacetRemove);
